import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './Icon.js';
import { Skeleton } from './Spinner.js';
import { formatCompact, formatCount } from './format.js';

export interface StatTileDelta {
  /** The formatted change, e.g. "+3" or "−12%". */
  text: string;
  direction: 'up' | 'down' | 'flat';
  /** Chosen by meaning, never by direction (04-data §3): overdue rising is bad. */
  tone: 'good' | 'bad' | 'neutral';
}

export interface StatTileProps {
  label: string;
  /** A number is formatted (abbreviated past 10,000 with the exact value in title and name). */
  value: number | string | null | undefined;
  /** Optional unit or qualifier after the value ("meetings"). */
  unit?: string;
  delta?: StatTileDelta;
  /** The comparison basis, required with a delta: "vs. previous 7 days". */
  basis?: string;
  /** Small supporting line under the value. */
  hint?: ReactNode;
  /** Makes the whole tile a link to the detail it summarises. */
  to?: string;
  loading?: boolean;
  /** Tone applied to the value itself, e.g. danger for an overdue count above zero. */
  valueTone?: 'default' | 'danger' | 'warning' | 'success';
  className?: string;
}

/** Label → value → delta with basis (04-data §3). Sits in a StatTileRow so tiles share a height. */
export function StatTile({ label, value, unit, delta, basis, hint, to, loading = false, valueTone = 'default', className }: StatTileProps) {
  const numeric = typeof value === 'number';
  const shown = loading ? null : numeric ? formatCompact(value) : value ?? '—';
  const exact = numeric ? formatCount(value) : undefined;
  const abbreviated = numeric && exact !== shown;
  const body = (
    <>
      <span className="stat-tile__label">{label}</span>
      {loading ? (
        <Skeleton width="4ch" height="1.6em" className="stat-tile__skeleton" />
      ) : (
        <span
          className={['stat-tile__value', 'num', `stat-tile__value--${valueTone}`].join(' ')}
          title={abbreviated ? exact : undefined}
          aria-label={abbreviated ? `${exact}${unit ? ` ${unit}` : ''}` : undefined}
        >
          {shown}
          {unit ? <span className="stat-tile__unit"> {unit}</span> : null}
        </span>
      )}
      {delta && !loading ? (
        <span className={`stat-tile__delta stat-tile__delta--${delta.tone}`}>
          <Icon name={delta.direction === 'up' ? 'arrow-up' : delta.direction === 'down' ? 'arrow-down' : 'minus'} size={14} />
          <span>{delta.text}</span>
          {basis ? <span className="muted"> {basis}</span> : null}
        </span>
      ) : null}
      {hint && !loading ? <span className="stat-tile__hint">{hint}</span> : null}
    </>
  );
  const classes = ['stat-tile', to ? 'stat-tile--link' : '', className ?? ''].filter(Boolean).join(' ');
  if (to) {
    return (
      <Link to={to} className={classes} aria-busy={loading || undefined}>
        {body}
      </Link>
    );
  }
  return (
    <div className={classes} aria-busy={loading || undefined}>
      {body}
    </div>
  );
}

export interface StatTileRowProps {
  children: ReactNode;
  /**
   * The content floor: the width the longest value needs at its type size (04-data §3). The row
   * derives the number of tiles per line from this and the measured container, never a breakpoint.
   */
  minWidth?: string;
  className?: string;
}

export function StatTileRow({ children, minWidth = '11rem', className }: StatTileRowProps) {
  return (
    <div className={['stat-tile-row', className].filter(Boolean).join(' ')} style={{ '--tile-min': minWidth } as CSSProperties}>
      {children}
    </div>
  );
}
